import { Suspense } from 'react'
import { Outlet } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { BrandSwitcher } from './BrandSwitcher'
import { ToastContainer } from './Toast'
import { RenderProgressWidget } from './RenderProgressWidget'
import { ErrorBoundary } from './ErrorBoundary'
import { TrialExpiredBanner } from './TrialExpiredBanner'
import { NoTrialBanner } from './NoTrialBanner'
import { StartTrialModal } from './StartTrialModal'
import { useBootstrap } from '@/hooks/useBootstrap'
import { useIsInternalAdmin } from '@/hooks/useTrialStatus'
import { useMixpanelIdentify, useMixpanelPageTrack } from '@/hooks/useMixpanel'
import { useIntercomBoot } from '@/hooks/useIntercom'
import { useTeamStore } from '@/stores/team'
import { useAuthStore } from '@/stores/auth'
import { hasNoPlan } from '@/lib/utils'

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
    </div>
  )
}

export function Layout() {
  const { teamLoading } = useBootstrap()
  useMixpanelIdentify()
  useMixpanelPageTrack()
  useIntercomBoot()

  const team = useTeamStore((s) => s.team)
  const isRealEstate = useTeamStore((s) => s.isRealEstate)
  const isInternalAdmin = useIsInternalAdmin()
  // teamAdmins (which useIsInternalAdmin reads from) doesn't update atomically with
  // selectedTeamId either — see switchingTeam's definition in stores/auth.ts. Without this,
  // switching into a team right after creating it (Add Account) could read isInternalAdmin as
  // false for a beat and flash StartTrialModal before NoTrialBanner took over.
  const switchingTeam = useAuthStore((s) => s.switchingTeam)
  // Real estate teams don't have billing/subscriptions at all (see SettingsPage's Subscription
  // card, which is hidden for them too) — never gate them on this. Also wait for GET_TEAM to
  // actually resolve — team.planName is null both before it's loaded and when it's genuinely
  // unset, and gating on the former flashes the modal on every login/refresh before the real
  // team data arrives.
  const noPlan = !isRealEstate && !teamLoading && !switchingTeam && hasNoPlan(team?.planName)

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      <TrialExpiredBanner />
      {noPlan && isInternalAdmin && <NoTrialBanner />}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <BrandSwitcher />
      <ToastContainer />
      <RenderProgressWidget />
      {noPlan && !isInternalAdmin && <StartTrialModal />}
    </div>
  )
}
